// ColumnFilterDropdown.tsx - Lightweight column header filter and sort
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Select from 'react-select';
import { DatePicker } from 'antd';
import moment from 'moment';
import { ChevronDown, SortAsc, SortDesc, Filter, X } from 'lucide-react';
import { operatorOptions } from '../../views/leads/LeadFilterUtils';
import './ColumnFilterDropdown.css';

export interface ColumnFilterConfig {
  operator?: any;
  value?: any;
  endValue?: any; // For "between" operator
}

export interface ColumnFilterDropdownProps {
  colId: string;
  headerName: string;
  fieldType?: string; // text, number, date, selection, etc.
  fieldOptions?: Array<{ label: string; value: string }>; // For selection/multi-selection fields
  currentFilter?: ColumnFilterConfig;
  currentSort?: 'asc' | 'desc' | null;
  onFilterApply: (colId: string, filter: ColumnFilterConfig | null) => void;
  onSort?: (colId: string, direction: 'asc' | 'desc') => void;
  allowSort?: boolean;
  allowFilter?: boolean;
}

// ✅ Preset date options (matching QuickFilters.tsx)
const PRESET_DATES = [
  { label: 'Today', getValue: () => ({
    start: moment.utc().startOf('day').format(),
    end: moment.utc().endOf('day').format()
  })},
  { label: 'Yesterday', getValue: () => ({
    start: moment.utc().subtract(1, 'd').startOf('day').format(),
    end: moment.utc().subtract(1, 'd').endOf('day').format()
  })},
  { label: 'This Week', getValue: () => {
    const dayOfWeek = moment.utc().day();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = moment.utc().add(diff, 'days');
    return {
      start: weekStart.startOf('day').format(),
      end: moment.utc().endOf('day').format()
    };
  }},
  { label: 'Last 7 Days', getValue: () => ({
    start: moment.utc().subtract(7, 'd').startOf('day').format(),
    end: moment.utc().endOf('day').format()
  })},
  { label: 'Last 30 Days', getValue: () => ({
    start: moment.utc().subtract(30, 'd').startOf('day').format(),
    end: moment.utc().endOf('day').format()
  })},
  { label: 'This Month', getValue: () => ({
    start: moment.utc().startOf('month').format(),
    end: moment.utc().endOf('day').format()
  })},
];

const ColumnFilterDropdown: React.FC<ColumnFilterDropdownProps> = ({
  colId,
  headerName,
  fieldType = 'text',
  fieldOptions = [],
  currentFilter,
  currentSort,
  onFilterApply,
  onSort,
  allowSort = true,
  allowFilter = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localOperator, setLocalOperator] = useState<any>(currentFilter?.operator);
  const [localValue, setLocalValue] = useState<any>(currentFilter?.value || '');
  const [localEndValue, setLocalEndValue] = useState<any>(currentFilter?.endValue || '');
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [showDatePresets, setShowDatePresets] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside (but allow DatePicker calendar to stay open)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if clicked on Ant Design DatePicker calendar or picker wrapper
      const isDatePickerClick =
        (target as Element).closest?.('.ant-picker-dropdown') ||
        (target as Element).closest?.('.ant-picker') ||
        (target as Element).closest?.('.rc-picker');

      // Check if clicked on Dropdown or Button
      const isDropdownClick =
        dropdownRef.current?.contains(target) ||
        buttonRef.current?.contains(target);

      // Only close if not clicking on dropdown, button, OR date picker
      if (!isDropdownClick && !isDatePickerClick) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Sync with props when they change
  useEffect(() => {
    setLocalOperator(currentFilter?.operator);

    // Handle different value formats
    let valueToSet: any = '';
    if (currentFilter?.value) {
      // For multi-selection, extract just the IDs from the stored objects
      if (Array.isArray(currentFilter.value)) {
        valueToSet = currentFilter.value.map((v: any) =>
          typeof v === 'object' ? v.value : v
        );
      } else if (typeof currentFilter.value === 'object' && currentFilter.value.value) {
        // For single selection with object format, extract the ID
        valueToSet = currentFilter.value.value;
      } else {
        // For plain string/number values
        valueToSet = currentFilter.value;
      }
    }

    setLocalValue(valueToSet);
    setLocalEndValue(currentFilter?.endValue || '');
  }, [currentFilter]);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;

      setDropdownPosition({
        top: rect.bottom + scrollY + 4, // 4px gap below button
        left: rect.left + scrollX,
        width: Math.max(rect.width, 280), // Min width 280px
      });
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  // Get operator options based on field type - MATCHES leadFilter.tsx exactly
  const getOperatorOptionsForField = (): Array<{ label: string; value: string }> => {
    switch (fieldType) {
      case 'name':
      case 'email':
      case 'text':
      case 'phone':
        return [
          { label: 'is', value: 'eq' },
          { label: 'contain', value: 'contain' }, // Match leadFilter.tsx (singular "contain")
          { label: 'does not contain', value: 'doesNotContain' },
          { label: 'is not', value: 'ne' },
        ];

      case 'number':
      case 'quantity':
        return [
          { label: 'is', value: 'eq' },
          { label: 'is not', value: 'ne' },
          { label: 'greater than', value: 'gt' },
          { label: 'greater than or equal', value: 'gte' },
          { label: 'less than', value: 'lt' },
          { label: 'less than or equal', value: 'lte' },
          { label: 'between', value: 'between' },
        ];

      case 'amount':
        return [
          { label: 'is', value: 'eq' },
          { label: 'is not', value: 'ne' },
          { label: 'greater than', value: 'gt' },
          { label: 'greater than or equal', value: 'gte' },
          { label: 'less than', value: 'lt' },
          { label: 'less than or equal', value: 'lte' },
          { label: 'between', value: 'between' },
        ];

      case 'date':
        return [
          { label: 'is', value: 'eq' },
          { label: 'is before', value: 'lt' },
          { label: 'is after', value: 'gt' },
          { label: 'between', value: 'between' },
        ];

      case 'selection':
        return [
          { label: 'is', value: 'eq' },
          { label: 'is not', value: 'ne' },
        ];

      case 'multi-selection':
        return [
          { label: 'contains', value: 'contain' }, // Singular to match backend
          { label: 'does not contain', value: 'doesNotContain' },
        ];

      default:
        return operatorOptions;
    }
  };

  const handleApplyFilter = () => {
    if (!localOperator || !localValue) {
      // Clear filter if incomplete
      onFilterApply(colId, null);
    } else {
      // Extract operator value - handle both string and object formats
      const operatorValue = typeof localOperator === 'string' ? localOperator : localOperator?.value || localOperator;

      // For multi-selection, convert value IDs back to full option objects so FilterStatusBar can display labels
      let valueToStore = localValue;
      if (fieldType === 'multi-selection' && Array.isArray(localValue)) {
        valueToStore = localValue.map((id: string) => {
          const option = fieldOptions.find((opt: any) => opt.value === id);
          return option ? { label: option.label || String(option.value), value: option.value } : { label: id, value: id };
        });
      } else if (fieldType === 'selection' && fieldOptions.length > 0) {
        // For single selection, also convert to object format with label
        const option = fieldOptions.find((opt: any) => opt.value === localValue);
        if (option) {
          valueToStore = { label: option.label || String(option.value), value: option.value };
        }
      }

      onFilterApply(colId, {
        operator: operatorValue,
        value: valueToStore,
        ...(operatorValue === 'between' && localEndValue ? { endValue: localEndValue } : {})
      });
    }
    setIsOpen(false);
  };

  const handleClearFilter = () => {
    setLocalOperator(undefined);
    setLocalValue('');
    setLocalEndValue('');
    onFilterApply(colId, null);
  };

  const handleSort = (direction: 'asc' | 'desc') => {
    if (onSort) {
      onSort(colId, direction);
    }
    setIsOpen(false);
  };

  // ✅ Helper to apply preset date
  const applyPresetDate = (preset: typeof PRESET_DATES[0]) => {
    const { start, end } = preset.getValue();
    setLocalValue(start);
    setLocalEndValue(end);
    setShowDatePresets(false);
  };

  // Helper to safely get operator value (handles both string and object formats)
  const getOperatorValue = (): string | undefined => {
    if (typeof localOperator === 'string') return localOperator;
    return localOperator?.value || localOperator;
  };

  const renderValueInput = () => {
    // For selection fields with predefined options
    if ((fieldType === 'selection' || fieldType === 'multi-selection') && fieldOptions.length > 0) {
      const opValue = getOperatorValue();
      // For multi-selection with "contain" or "doesNotContain", show multi-select
      if (fieldType === 'multi-selection' && localOperator &&
          (opValue === 'contain' || opValue === 'doesNotContain')) {
        // Convert stored value IDs to React-Select format with labels
        const selectedValues = Array.isArray(localValue)
          ? localValue.map((id: string) => {
              const option = fieldOptions.find((opt: any) => opt.value === id);
              return option ? { label: option.label || String(option.value), value: option.value } : { label: id, value: id };
            })
          : [];

        return (
          <Select
            value={selectedValues}
            options={fieldOptions}
            onChange={(options: any) => setLocalValue(options ? options.map((opt: any) => opt.value) : [])}
            placeholder="Select values..."
            isClearable
            isMulti
            className="value-select"
            classNamePrefix="select"
          />
        );
      }

      // For regular selection or multi-selection with "is/is not"
      // Convert stored value ID to React-Select format with label
      let selectedOption = null;
      if (localValue) {
        const option = fieldOptions.find((opt: any) => opt.value === localValue);
        selectedOption = option ? { label: option.label || String(option.value), value: option.value } : { label: localValue, value: localValue };
      }

      return (
        <Select
          value={selectedOption}
          options={fieldOptions}
          onChange={(option: any) => setLocalValue(option?.value || '')}
          placeholder="Select value..."
          isClearable
          className="value-select"
          classNamePrefix="select"
        />
      );
    }

    // For date fields
    if (fieldType === 'date') {
      return (
        <div className="date-inputs">
          {/* ✅ Preset dates option */}
          <button
            className="preset-dates-toggle"
            onClick={() => setShowDatePresets(!showDatePresets)}
            type="button"
          >
            📅 Quick dates {showDatePresets ? '▼' : '▶'}
          </button>

          {showDatePresets && (
            <div className="preset-dates-grid">
              {PRESET_DATES.map((preset) => (
                <button
                  key={preset.label}
                  className="preset-date-btn"
                  onClick={() => applyPresetDate(preset)}
                  type="button"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}

          <label className="date-input-label">Start Date:</label>
          <DatePicker
            format="DD MMM YYYY"
            value={localValue ? moment(localValue) : null}
            onChange={(date) => setLocalValue(date ? moment(date).format() : '')}
            placeholder="Select start date"
            style={{ width: '100%' }}
          />

          {getOperatorValue() === 'between' && (
            <>
              <label className="date-input-label">End Date:</label>
              <DatePicker
                format="DD MMM YYYY"
                value={localEndValue ? moment(localEndValue) : null}
                onChange={(date) => setLocalEndValue(date ? moment(date).format() : '')}
                placeholder="Select end date"
                style={{ width: '100%' }}
              />
            </>
          )}
        </div>
      );
    }

    // For number/amount fields
    if (fieldType === 'number' || fieldType === 'amount') {
      const opValue = getOperatorValue();
      return (
        <div className="number-inputs">
          <input
            type="number"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder={opValue === 'between' ? 'Start value' : 'Enter value'}
            className="value-input"
          />
          {opValue === 'between' && (
            <>
              <span className="range-separator">to</span>
              <input
                type="number"
                value={localEndValue}
                onChange={(e) => setLocalEndValue(e.target.value)}
                placeholder="End value"
                className="value-input"
              />
            </>
          )}
        </div>
      );
    }

    // Default: text input
    return (
      <input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder="Enter value"
        className="value-input"
      />
    );
  };

  const hasActiveFilter = currentFilter && currentFilter.operator && currentFilter.value;
  const hasActiveSort = currentSort !== null;

  // Render dropdown menu
  const renderDropdownMenu = () => {
    if (!isOpen || !dropdownPosition) return null;

    const menuContent = (
      <div
        ref={dropdownRef}
        className="column-filter-dropdown-menu"
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          minWidth: `${dropdownPosition.width}px`,
        }}
      >
          {/* Sort Section */}
          {allowSort && onSort && (
            <div className="dropdown-section">
              <div className="section-title">Sort</div>
              <button
                className={`menu-item ${currentSort === 'asc' ? 'active' : ''}`}
                onClick={() => handleSort('asc')}
              >
                <SortAsc size={14} />
                <span>Sort A → Z</span>
              </button>
              <button
                className={`menu-item ${currentSort === 'desc' ? 'active' : ''}`}
                onClick={() => handleSort('desc')}
              >
                <SortDesc size={14} />
                <span>Sort Z → A</span>
              </button>
            </div>
          )}

          {/* Filter Section */}
          {allowFilter && (
            <>
              <div className="dropdown-divider" />
              <div className="dropdown-section">
                <div className="section-title">
                  <Filter size={14} />
                  <span>Filter</span>
                </div>

                {/* Operator Selection */}
                <div className="filter-field">
                  <label>Condition</label>
                  <Select
                    value={localOperator ? { label: localOperator.label || localOperator, value: localOperator.value || localOperator } : null}
                    options={getOperatorOptionsForField()}
                    onChange={(option: any) => setLocalOperator(option || undefined)}
                    placeholder="Select condition..."
                    isClearable
                    className="operator-select"
                    classNamePrefix="select"
                  />
                </div>

                {/* Value Input */}
                {localOperator && (
                  <div className="filter-field">
                    <label>Value</label>
                    {renderValueInput()}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="filter-actions">
                  <button
                    className="btn-clear"
                    onClick={handleClearFilter}
                    disabled={!hasActiveFilter}
                  >
                    <X size={14} />
                    Clear
                  </button>
                  <button
                    className="btn-apply"
                    onClick={handleApplyFilter}
                    disabled={!localOperator || !localValue}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </>
          )}
      </div>
    );

    // Use portal to render dropdown at body level (avoids overflow issues)
    return ReactDOM.createPortal(menuContent, document.body);
  };

  return (
    <div className="column-filter-dropdown-container">
      <button
        ref={buttonRef}
        className={`column-header-button ${hasActiveFilter || hasActiveSort ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        title={`Filter and sort ${headerName}`}
      >
        <span className="header-text">{headerName}</span>
        <ChevronDown
          size={14}
          className={`dropdown-icon ${isOpen ? 'open' : ''}`}
        />
        {(hasActiveFilter || hasActiveSort) && (
          <span className="active-indicator" />
        )}
      </button>

      {renderDropdownMenu()}
    </div>
  );
};

export default ColumnFilterDropdown;
